// Lo que «Arma la venta» sabe de la venta en caché: nombre corto de cada línea y promos de crédito del equipo.
import { useQueryClient } from '@tanstack/react-query';
import { WIZARD_KEYS } from '@/domains/sales/wizard/api/useWizard';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { sureKeepFinances } from '@/domains/sales/wizard/cardBenefits';
import type { CreditGroupNote } from '@/domains/sales/wizard/components/CreditPromotionsGroup';
import {
    creditPromotionRows,
    creditPromotionsForItem,
    type CreditPromotionRow,
} from '@/domains/sales/wizard/creditPromotions';
import type {
    CatalogItem,
    CatalogResponse,
    PlanLevel,
    ReservedLine,
    WizardSession,
} from '@/domains/sales/wizard/types';

export interface AnchorSaleContext {
    /** Nombre corto por línea, para el desglose del descuento. */
    lineNames: ReadonlyMap<string, string>;
    /** Promos de crédito del equipo; vacío si SureKeep no lo financia. */
    creditRows: CreditPromotionRow[];
    creditNote: CreditGroupNote;
    /** La venta va sin SureKeep: no financia el equipo o el cliente no califica. */
    withoutSureKeep: boolean;
    /** Nivel resuelto del cliente; `undefined` mientras el score no lo da. */
    creditLevel: PlanLevel | undefined;
    /** Categoría y gama de un SKU según el catálogo cargado. */
    catalogInfo: (
        sku: string,
    ) => { category?: CatalogItem['category']; tier: string; name: string } | undefined;
}

const shortName = (line: ReservedLine): string => line.product?.model || line.sku;

/** Lee la sesión y el catálogo ya cargados; no los pide. */
export function useAnchorSaleContext(
    sessionId: string,
    anchorLine: ReservedLine,
): AnchorSaleContext {
    const queryClient = useQueryClient();
    const session = queryClient.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
    const catalog = queryClient.getQueryData<CatalogResponse>(WIZARD_KEYS.catalog(sessionId));
    const lines = session?.reservedLines ?? [anchorLine];
    const lineNames = new Map(lines.map((l) => [l.lineId, shortName(l)]));
    lineNames.set(anchorLine.lineId, shortName(anchorLine));

    const level = session?.loan?.level;
    const notEligible = level === 'NOT_ELIGIBLE';
    const resolved = level === 'BRONZE' || level === 'SILVER' || level === 'GOLD';
    const item = catalog?.items.find((i) => i.sku === anchorLine.sku);
    const creditRows =
        item && sureKeepFinances(anchorLine)
            ? creditPromotionsForItem(
                  creditPromotionRows(
                      catalog?.scopeWidePromotions ?? [],
                      [item],
                      // Solo las de su nivel; sin nivel o sin calificar van todas, para la nota del grupo.
                      resolved ? level : undefined,
                  ),
                  item,
              )
            : [];
    return {
        lineNames,
        creditRows,
        creditNote: notEligible ? 'notEligible' : resolved ? 'atQuote' : 'levelPending',
        withoutSureKeep: notEligible || !sureKeepFinances(anchorLine),
        creditLevel: resolved ? level : undefined,
        catalogInfo: (sku) => {
            const found = catalog?.items.find((i) => i.sku === sku);
            return found
                ? { category: found.category, tier: found.tier, name: itemDisplayName(found) }
                : undefined;
        },
    };
}
